/**
 * C4 — device and conditions capture for a launch monitor import.
 *
 * Self-contained on purpose: it owns its own state shape and renders as one
 * delimited block, so it can be dropped into the import review modal without
 * entangling with the rest of that modal's JSX.
 *
 * Why it exists: launch_monitor_brand is NULL on all 153 non-synthetic profile
 * rows, so no carry number in the database has a device or an elevation attached
 * to it. A carry without those is not a measurement, and the shot table now has
 * columns for exactly this.
 */

import React from 'react';
import { Environment, ShotConditions, SourceUnits } from '../../services/launchMonitorShotService';

export interface Props {
  value: ShotConditions;
  onChange: (next: ShotConditions) => void;
  /** Brand already chosen upstream for parsing, used as the default device. */
  suggestedBrand?: string;
}

const DEVICE_OPTIONS = [
  { value: 'trackman', label: 'TrackMan' },
  { value: 'gcquad', label: 'Foresight GCQuad' },
  { value: 'gchawk', label: 'Foresight GCHawk' },
  { value: 'mevo_plus', label: 'FlightScope Mevo+' },
  { value: 'garmin', label: 'Garmin Approach' },
  { value: 'skytrak', label: 'SkyTrak' },
  { value: 'uneekor', label: 'Uneekor' },
  { value: 'full_swing', label: 'Full Swing' },
  { value: 'other', label: 'Other' },
];

export const emptyConditions = (suggestedBrand?: string): ShotConditions => ({
  device_brand: suggestedBrand || '',
  source_units: 'imperial',
  environment: 'indoor',
});

/** Mirrors the backend rule: altitude is required outdoors, where it moves carry. */
export function conditionsError(c: ShotConditions): string | null {
  if (!c.device_brand) return 'Select the launch monitor this session was measured on.';
  if (c.environment === 'outdoor' && (c.altitude_ft === undefined || Number.isNaN(c.altitude_ft))) {
    return 'Altitude is required for outdoor sessions — carry cannot be compared without it.';
  }
  return null;
}

const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
const inputCls =
  'w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-1 ' +
  'focus:ring-blue-500 focus:border-blue-500';

const ShotConditionsFields: React.FC<Props> = ({ value, onChange, suggestedBrand }) => {
  const set = <K extends keyof ShotConditions>(key: K, v: ShotConditions[K]) =>
    onChange({ ...value, [key]: v });

  const numeric = (raw: string): number | undefined => {
    if (raw === '') return undefined;
    const n = parseFloat(raw);
    return Number.isNaN(n) ? undefined : n;
  };

  const error = conditionsError(value);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-800">Session conditions</h4>
        <p className="text-xs text-gray-500 mt-0.5">
          Stored with every shot. Without a device and an elevation, a carry number
          can&apos;t be compared against another session.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor="lm-device-brand">
            Launch monitor <span className="text-red-500">*</span>
          </label>
          <select
            id="lm-device-brand"
            className={inputCls}
            value={value.device_brand}
            onChange={(e) => set('device_brand', e.target.value)}
          >
            <option value="">Select…</option>
            {DEVICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {suggestedBrand && !value.device_brand && (
            <p className="text-xs text-blue-600 mt-1">
              Suggested: {suggestedBrand}
            </p>
          )}
        </div>

        <div>
          <label className={labelCls} htmlFor="lm-device-model">Model (optional)</label>
          <input
            id="lm-device-model"
            className={inputCls}
            type="text"
            placeholder="e.g. TrackMan 4"
            value={value.device_model ?? ''}
            onChange={(e) => set('device_model', e.target.value || undefined)}
          />
        </div>

        <div>
          <label className={labelCls} htmlFor="lm-units">
            Units as displayed <span className="text-red-500">*</span>
          </label>
          <select
            id="lm-units"
            className={inputCls}
            value={value.source_units}
            onChange={(e) => set('source_units', e.target.value as SourceUnits)}
          >
            <option value="imperial">Imperial (yds / mph)</option>
            <option value="metric">Metric (m / kph)</option>
          </select>
        </div>

        <div>
          <label className={labelCls} htmlFor="lm-environment">
            Environment <span className="text-red-500">*</span>
          </label>
          <select
            id="lm-environment"
            className={inputCls}
            value={value.environment}
            onChange={(e) => set('environment', e.target.value as Environment)}
          >
            <option value="indoor">Indoor / simulator</option>
            <option value="outdoor">Outdoor range</option>
          </select>
        </div>

        <div>
          <label className={labelCls} htmlFor="lm-altitude">
            Altitude (ft)
            {value.environment === 'outdoor' && <span className="text-red-500"> *</span>}
          </label>
          <input
            id="lm-altitude"
            className={inputCls}
            type="number"
            placeholder={value.environment === 'outdoor' ? 'Required' : 'Optional indoors'}
            value={value.altitude_ft ?? ''}
            onChange={(e) => set('altitude_ft', numeric(e.target.value))}
          />
        </div>

        <div>
          <label className={labelCls} htmlFor="lm-temperature">Temperature (°F)</label>
          <input
            id="lm-temperature"
            className={inputCls}
            type="number"
            placeholder="Optional"
            value={value.temperature_f ?? ''}
            onChange={(e) => set('temperature_f', numeric(e.target.value))}
          />
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 mt-3">{error}</p>
      )}
    </div>
  );
};

export default ShotConditionsFields;
